import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
  PlayArrow as TryIcon,
  Code as CodeIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

interface Parameter {
  name: string;
  in: 'query' | 'path' | 'header' | 'body';
  description?: string;
  required?: boolean;
  type?: string;
  schema?: any;
  example?: any;
}

interface Response {
  statusCode: string;
  description: string;
  schema?: any;
  example?: any;
}

interface CodeExample {
  language: string;
  code: string;
}

interface ApiEndpoint {
  method: string;
  path: string;
  summary?: string;
  description?: string;
  parameters?: Parameter[];
  requestBody?: {
    description?: string;
    required?: boolean;
    content?: any;
  };
  responses?: Response[];
  tags?: string[];
  security?: any[];
}

interface ApiDocViewerProps {
  content: {
    endpoint?: ApiEndpoint;
    endpoints?: ApiEndpoint[];
    codeExamples?: CodeExample[];
    baseUrl?: string;
    title?: string;
    description?: string;
  };
  enableTryIt?: boolean;
  onComplete?: () => void;
}

const METHOD_COLORS: Record<string, string> = {
  get: '#61affe',
  post: '#49cc90',
  put: '#fca130',
  patch: '#50e3c2',
  delete: '#f93e3e',
  options: '#0d5aa7',
  head: '#9012fe',
};

function CodeBlock({ code, language }: { code: string; language: string }) {
  const { enqueueSnackbar } = useSnackbar();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      enqueueSnackbar('Code kopiert!', { variant: 'success' });
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      enqueueSnackbar('Fehler beim Kopieren', { variant: 'error' });
    }
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: 'grey.800',
          color: 'grey.300',
          px: 2,
          py: 0.5,
          borderRadius: '4px 4px 0 0',
        }}
      >
        <Typography variant="caption">{language}</Typography>
        <Tooltip title={copied ? 'Kopiert!' : 'Kopieren'}>
          <IconButton size="small" onClick={handleCopy} sx={{ color: 'grey.300' }}>
            {copied ? <CheckIcon fontSize="small" /> : <CopyIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        component="pre"
        sx={{
          bgcolor: 'grey.900',
          color: 'grey.100',
          p: 2,
          m: 0,
          overflow: 'auto',
          borderRadius: '0 0 4px 4px',
          fontSize: '0.875rem',
          fontFamily: 'monospace',
        }}
      >
        <code>{code}</code>
      </Box>
    </Box>
  );
}

function EndpointCard({ endpoint, codeExamples }: { endpoint: ApiEndpoint; codeExamples?: CodeExample[] }) {
  const [expanded, setExpanded] = useState(true);
  const [codeTab, setCodeTab] = useState(0);

  const method = endpoint.method.toLowerCase();
  const methodColor = METHOD_COLORS[method] || '#999';

  // Get request body schema if exists
  const requestBodySchema = endpoint.requestBody?.content?.['application/json']?.schema;

  return (
    <Paper sx={{ mb: 3, overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          p: 2,
          cursor: 'pointer',
          bgcolor: expanded ? 'action.selected' : 'inherit',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Chip
          label={endpoint.method.toUpperCase()}
          size="small"
          sx={{
            bgcolor: methodColor,
            color: 'white',
            fontWeight: 'bold',
            minWidth: 70,
          }}
        />
        <Typography variant="body1" fontFamily="monospace" fontWeight={500}>
          {endpoint.path}
        </Typography>
        {endpoint.summary && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', mr: 2 }}>
            {endpoint.summary}
          </Typography>
        )}
        <ExpandMoreIcon
          sx={{
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        />
      </Box>

      {expanded && (
        <Box sx={{ p: 2, pt: 0 }}>
          <Divider sx={{ mb: 2 }} />

          {/* Description */}
          {endpoint.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {endpoint.description}
            </Typography>
          )}

          {/* Tags */}
          {endpoint.tags && endpoint.tags.length > 0 && (
            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
              {endpoint.tags.map((tag) => (
                <Chip key={tag} label={tag} size="small" variant="outlined" />
              ))}
            </Box>
          )}

          {/* Parameters */}
          {endpoint.parameters && endpoint.parameters.length > 0 && (
            <Accordion defaultExpanded sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Parameter</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>In</TableCell>
                        <TableCell>Typ</TableCell>
                        <TableCell>Erforderlich</TableCell>
                        <TableCell>Beschreibung</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {endpoint.parameters.map((param, idx) => (
                        <TableRow key={idx}>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {param.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={param.in} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontFamily="monospace">
                              {param.type || param.schema?.type || 'string'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {param.required ? (
                              <Chip label="Ja" size="small" color="error" />
                            ) : (
                              <Typography variant="body2" color="text.secondary">Nein</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{param.description || '-'}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}

          {/* Request Body */}
          {requestBodySchema && (
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Request Body</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {endpoint.requestBody?.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {endpoint.requestBody.description}
                  </Typography>
                )}
                <CodeBlock
                  code={JSON.stringify(requestBodySchema, null, 2)}
                  language="JSON Schema"
                />
              </AccordionDetails>
            </Accordion>
          )}

          {/* Responses */}
          {endpoint.responses && endpoint.responses.length > 0 && (
            <Accordion sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle2">Responses</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {endpoint.responses.map((resp, idx) => (
                  <Box key={idx} sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Chip
                        label={resp.statusCode}
                        size="small"
                        color={resp.statusCode.startsWith('2') ? 'success' : resp.statusCode.startsWith('4') ? 'warning' : 'error'}
                      />
                      <Typography variant="body2">{resp.description}</Typography>
                    </Box>
                    {resp.example && (
                      <CodeBlock
                        code={typeof resp.example === 'string' ? resp.example : JSON.stringify(resp.example, null, 2)}
                        language="JSON"
                      />
                    )}
                  </Box>
                ))}
              </AccordionDetails>
            </Accordion>
          )}

          {/* Code Examples */}
          {codeExamples && codeExamples.length > 0 && (
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Code-Beispiele
              </Typography>
              <Tabs
                value={codeTab}
                onChange={(_, v) => setCodeTab(v)}
                sx={{ mb: 1 }}
              >
                {codeExamples.map((ex, idx) => (
                  <Tab key={idx} label={ex.language} icon={<CodeIcon />} iconPosition="start" />
                ))}
              </Tabs>
              {codeExamples.map((ex, idx) => (
                <Box key={idx} hidden={codeTab !== idx}>
                  <CodeBlock code={ex.code} language={ex.language} />
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
}

export default function ApiDocViewer({ content, enableTryIt = false, onComplete }: ApiDocViewerProps) {
  // Get endpoints - support both single endpoint and multiple
  const endpoints: ApiEndpoint[] = content.endpoint
    ? [content.endpoint]
    : content.endpoints || [];

  if (endpoints.length === 0) {
    return (
      <Alert severity="info">
        Keine API-Dokumentation verfuegbar.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1000, mx: 'auto' }}>
      {/* Header */}
      {content.title && (
        <Typography variant="h4" gutterBottom>
          {content.title}
        </Typography>
      )}
      {content.description && (
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {content.description}
        </Typography>
      )}
      {content.baseUrl && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Base URL:</strong> <code>{content.baseUrl}</code>
          </Typography>
        </Alert>
      )}

      {/* Endpoints */}
      {endpoints.map((endpoint, idx) => (
        <EndpointCard
          key={idx}
          endpoint={endpoint}
          codeExamples={content.codeExamples}
        />
      ))}

      {/* Complete Button */}
      {onComplete && (
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Button variant="contained" onClick={onComplete} size="large">
            Lektion abschliessen
          </Button>
        </Box>
      )}
    </Box>
  );
}
